import { useEffect, useRef, useState } from "react";
import {
  ensureEvalPreCaptureCountdownDuration,
  getEvalPreCaptureCountdownLeadSec,
  maybePlayEvalPreCaptureCountdown,
  resetEvalPreCaptureCountdownDedupe,
} from "../utils/evalPreCaptureCountdown";
import { getEffectiveClientCaptureSettings } from "../utils/liveTrainingSettings";

/**
 * Plays the countdown MP3 `audioDuration` seconds before the autoeval capture
 * window opens, so recording starts when the sound ends.
 * Skips when sequence duration <= W.
 */
export function useEvalPreCaptureCountdown({
  enabled = true,
  tsId = null,
  teId = null,
  sequenceNumber = null,
  sequenceRemainingSec = null,
  sequenceDurationSec = null,
  autoevalDurationSec = null,
}) {
  const playedForKeyRef = useRef(null);
  const [durationReadyToken, setDurationReadyToken] = useState(0);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    ensureEvalPreCaptureCountdownDuration().then(() => {
      if (!cancelled) setDurationReadyToken((n) => n + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    const w =
      autoevalDurationSec != null && Number.isFinite(Number(autoevalDurationSec))
        ? Number(autoevalDurationSec)
        : getEffectiveClientCaptureSettings(tsId).autoevalVideoMaxDurationSec;

    const playKey = `${teId || ""}:${sequenceNumber ?? ""}`;
    const rem = Number(sequenceRemainingSec);
    const duration = Number(sequenceDurationSec);

    if (!Number.isFinite(rem) || rem <= 0) return;
    if (!Number.isFinite(duration) || duration <= 0) return;
    if (duration <= w) {
      playedForKeyRef.current = null;
      return;
    }

    const leadSec = getEvalPreCaptureCountdownLeadSec();
    const cueAt = w + leadSec;
    if (rem > cueAt) {
      if (playedForKeyRef.current === playKey) {
        playedForKeyRef.current = null;
      }
      return;
    }
    if (rem <= w) return;
    if (playedForKeyRef.current === playKey) return;

    const started = maybePlayEvalPreCaptureCountdown({
      sequenceRemainingSec: rem,
      sequenceDurationSec: duration,
      autoevalDurationSec: w,
      playKey,
      enabled: true,
      onBlocked: () => {
        if (playedForKeyRef.current === playKey) {
          playedForKeyRef.current = null;
        }
      },
    });
    if (started) playedForKeyRef.current = playKey;
  }, [
    enabled,
    tsId,
    teId,
    sequenceNumber,
    sequenceRemainingSec,
    sequenceDurationSec,
    autoevalDurationSec,
    durationReadyToken,
  ]);

  useEffect(() => {
    return () => {
      playedForKeyRef.current = null;
    };
  }, [teId]);
}

export { resetEvalPreCaptureCountdownDedupe };
